"use client";

import { useState } from "react";
import styles from "./page.module.css";
import { companyConfig } from "../lib/config";

const navLinks = [
  { name: "Solutions", hasDropdown: true },
  { name: "Resources", hasDropdown: true },
  { name: "Documentation", hasDropdown: false },
  { name: "Company", hasDropdown: false },
  { name: "Pricing", hasDropdown: false },
];

const stats = [
  { value: "98%", label: "Containment Rate for inbound support calls—handle routine queries without human intervention." },
  { value: "85%", label: "Cost savings on reservation staffing with 24/7 automated handling." },
  { value: "500ms", label: "Response latency for natural, interruption-free conversations." },
  { value: "3x", label: "Booking conversion boost from guided search to secure checkout." },
];

const capabilities = [
  {
    title: "Custom Airline Branding",
    description: "Tailored logos, domains, themes, and voice personas—infused with your brand's tone for authentic passenger interactions.",
    icon: "/images/custome_airline_branding.png",
  },
  {
    title: "Ready-to-Fly Components",
    description: "Pre-built UI blocks for itineraries, analytics, call logs, and PNR tracking optimized for aviation compliance.",
    icon: "/images/ready_to_fly.png",
  },
  {
    title: "Enterprise-Grade Security",
    description: "HIPAA/GDPR compliant, scalable for high-volume peaks, and built for B2B airline ecosystems.",
    icon: "/images/Enterprise_Grade_Security.png",
  },
  {
    title: "For Airlines & Travel Tech",
    description: "Embed voice AI into your reservation systems, fully branded and integrated for seamless journeys.",
    icon: "/images/For_Airlines_&_Travel_Tech.png",
  },
];

const workflow = [
  {
    title: "Make Agents Airline-Ready",
    description:
      "Personalize with real-time PNR data, drag-and-drop workflows for intents, and A/B testing for fare rule optimizations.",
    action: "Explore Dialog Control",
    image: "/images/blue_sphere.jpg",
    size: "tall",
    gridArea: "topLeft",
  },
  {
    title: "Feels Like Cabin Crew",
    description:
      "Natural conversations with barge-in support, 30+ languages/accents, and proactive clarifications. Handles interruptions, topic shifts, and airline-specific slang flawlessly.",
    action: "Learn About Our Voice I/O",
    image: "/images/cabin.jpg",
    size: "wide",
    gridArea: "topRight",
    imageAsBackground: true,
  },
  {
    title: "Scales Like Your Fleet",
    description:
      "Auto-scale for millions of queries during holidays. Rule-based guardrails prevent errors, with effortless tool calls to booking APIs.",
    action: "View Pricing & Scalability",
    image: "/images/map.png",
    size: "wide",
    gridArea: "bottomLeft",
    imageAsBackground: true,
    variant: "map",
    badges: [] as { value: string; top: string; left: string | undefined; right: string | undefined }[],
  },
  {
    title: "Always In-Control Monitoring",
    description:
      "Track FTR, containment, latency, WER, handover rates, and CSAT via dashboards. Auto-QA transcripts (PII-redacted) for continuous improvement.",
    action: "Dive Into Analytics",
    image: "/images/binocolur_camera.png",
    size: "tall",
    gridArea: "bottomRight",
  },
];

const testimonial = {
  quote:
    "We've cut no-show rates by 40% with automated reminders and rebookings. The confidence-based handoffs are a game-changer—passengers never feel lost in the system.",
  author: "Amir Hashemi",
  role: "CEO Avtra",
};

const ratingStars = Array.from({ length: 5 });

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className={styles.page}>
      <div className={styles.heroBackdrop} aria-hidden="true" />

      <header className={styles.nav}>
        <Logo />
        <button
          className={styles.mobileMenuButton}
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`${styles.hamburger} ${mobileMenuOpen ? styles.hamburgerOpen : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        <nav className={`${styles.navLinks} ${mobileMenuOpen ? styles.navLinksOpen : ''}`} aria-label="Primary">
          {navLinks.map((link) => (
            <a key={link.name} href="#">
              {link.name}
              {link.hasDropdown && <span className={styles.dropdownChevron}>▾</span>}
            </a>
          ))}
          <div className={styles.navActionsMobile}>
            <button className={styles.ghostButton} type="button">
              CONTACT SALES
            </button>
            <button
              className={styles.solidButton}
              type="button"
              onClick={() => window.location.href = companyConfig.dashboardUrl}
            >
              JOIN / LOG IN
            </button>
          </div>
        </nav>
        <div className={styles.navActions}>
          <button className={styles.ghostButton} type="button">
            CONTACT SALES
          </button>
          <button
            className={styles.solidButton}
            type="button"
            onClick={() => window.location.href = companyConfig.dashboardUrl}
          >
            JOIN / LOG IN
          </button>
        </div>
      </header>

      <main>
        <section className={styles.hero}>
          <p className={styles.heroEyebrow}>Create voice-enabled chatbots for flight search, booking, and support.</p>
          <h1 className={styles.heroTitle}>Build Smarter Airline Experiences</h1>
          <p className={styles.heroSubtitle}>
            Deliver seamless, 24/7 conversations that feel human and drive conversions.
          </p>
          <div className={styles.emailBar}>
            <input type="email" placeholder="Your work email" aria-label="Work email" />
            <button type="button">Talk to our AI Experts</button>
          </div>
          <div className={styles.ratingRow}>
            <span className={styles.ratingIcon}>✈</span>
            <div className={styles.stars} aria-label="5 star rating">
              {ratingStars.map((_, index) => (
                <span key={index}>★</span>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.ticketSection}>
          {/* Cloud decorations */}
          <div className={styles.cloudLeft} aria-hidden="true" />
          <div className={styles.cloudRight} aria-hidden="true" />

          <div className={styles.ticketCard}>
            <div className={styles.ticketBody}>
              <div className={styles.ticketOverlay} aria-hidden="true" />
              <h2 className={styles.ticketTitle}>Dream Higher.<br />Book Faster.</h2>
              <p className={styles.ticketCopy}>
                Every tool you need to build, deploy, and scale enterprise-grade voice AI for airline operations.
              </p>
              {/* Airplane breaking out of card */}
              <div className={styles.airplaneContainer}>
                <img src="/images/airplane.png" alt="Airplane" className={styles.airplaneImage} />
              </div>
            </div>
            <div className={styles.ticketStub}>
              <div className={styles.ticketLogo}>
                <img src="/images/blue_logo.png" alt={`${companyConfig.name} emblem`} className={styles.stubLogoImage} />
              </div>
              <div className={styles.barcodeContainer}>
                <img src="/images/barcode.png" alt="Ticket barcode" className={styles.barcodeImage} />
              </div>
            </div>
            {/* Decorative notches */}
            <div className={styles.notchTop} aria-hidden="true" />
            <div className={styles.notchBottom} aria-hidden="true" />
          </div>

          <div className={styles.statsGrid}>
            {stats.map((stat) => (
              <div key={stat.value} className={styles.statCard}>
                <p className={styles.statValue}>{stat.value}</p>
                <p className={styles.statLabel}>{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>Launch Your Branded Airline Voice Platform</h2>
          <p className={styles.sectionDescription}>
            White-label {companyConfig.name} dashboards for your teams, partners, and customers. Empower developers to ship flight agents faster with intuitive,
            airline-specific workflows.
          </p>
          <div className={styles.capabilityGrid}>
            {capabilities.map((item) => (
              <article key={item.title} className={styles.capabilityCard}>
                <div className={styles.capabilityIcon} aria-hidden="true">
                  <img src={item.icon} alt={item.title} className={styles.capabilityIconImage} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.workflowSection}>
          <h2 className={styles.sectionHeading}>How {companyConfig.name} Works</h2>
          <p className={styles.sectionDescription}>
            Start with one agent for bookings or orchestrate your entire contact center. Integrates with Sabre, Amadeus, and major CRMs.
          </p>
          <div className={styles.workflowGrid}>
            {workflow.map((item) => {
              const isMapCard = item.variant === 'map';

              return (
                <article
                  key={item.title}
                  className={`${styles.workflowCard} ${item.size === 'tall' ? styles.workflowCardTall : styles.workflowCardWide} ${item.imageAsBackground ? styles.workflowCardWithBg : ''} ${item.gridArea === 'bottomRight' ? styles.workflowCardBottomRight : ''} ${isMapCard ? styles.workflowCardMap : ''}`}
                  style={{
                    gridArea: item.gridArea,
                    ...(item.imageAsBackground && { backgroundImage: `url(${item.image})` }),
                  }}
                >
                  <div className={styles.workflowContent}>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <button type="button" className={styles.linkButton}>
                      {item.action} →
                    </button>
                  </div>
                  {isMapCard && item.badges && (
                    <div className={styles.mapBadges} aria-hidden="true">
                      {item.badges.map((badge) => (
                        <span
                          key={badge.value}
                          className={styles.mapBadge}
                          style={{ top: badge.top, left: badge.left, right: badge.right }}
                        >
                          {badge.value}
                        </span>
                      ))}
                    </div>
                  )}
                  {!item.imageAsBackground && (
                    <div className={styles.workflowImageContainer}>
                      <img src={item.image} alt="" className={styles.workflowImage} />
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        <section className={styles.testimonialSection}>
          <h2 className={styles.testimonialHeading}>Trusted By Aviation Leaders</h2>
          <p className={styles.testimonialQuote}>{testimonial.quote}</p>
          <p className={styles.testimonialAuthor}>{testimonial.author}</p>
          <p className={styles.testimonialRole}>{testimonial.role}</p>
          <div className={styles.testimonialAvatars}>
            <img src="/images/person1.jpg" alt="" className={styles.testimonialAvatar} />
            <img src="/images/person2.jpg" alt="Co-Founder & Director of AI" title="Co-Founder & Director of AI" className={styles.testimonialAvatar} />
            <img src="/images/person3.png" alt="" className={`${styles.testimonialAvatar} ${styles.testimonialAvatarMain}`} />
            <img src="/images/person4.jpg" alt="Co-Founder & CTO" title="Co-Founder & CTO" className={styles.testimonialAvatar} />
            <img src="/images/person5.jpg" alt="" className={styles.testimonialAvatar} />
          </div>
        </section>

        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h3 className={styles.ctaTitle}>Take Flight with {companyConfig.name}</h3>
            <p className={styles.ctaCopy}>
              Ready to demo an intelligent booking agent?<br />
              Book a call and see it handle a live flight search.
            </p>
            <form className={styles.ctaForm}>
              <label htmlFor="cta-name" className="sr-only">
                Name
              </label>
              <input id="cta-name" type="text" placeholder="Name" />

              <label htmlFor="cta-email" className="sr-only">
                Email
              </label>
              <input id="cta-email" type="email" placeholder="Email" />

              <label htmlFor="cta-size" className="sr-only">
                Airline Size
              </label>
              <select id="cta-size" defaultValue="">
                <option value="" disabled>
                  Airline Size
                </option>
                <option value="regional">Regional (under 100 routes)</option>
                <option value="global">Global carrier</option>
                <option value="charter">Charter / Private</option>
              </select>

              <button type="button">Talk to our AI Experts</button>
            </form>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <Logo />
        <div className={styles.footerLinks}>
          <a href="#">Privacy Policy</a>
          <a href="#">Terms of Service</a>
          <a href="#">Contact Us</a>
        </div>
        <p className={styles.footerNote}>
          Revolutionizing Airline Conversations © {new Date().getFullYear()} {companyConfig.name} Inc. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

function Logo() {
  return (
    <div className={styles.logo}>
      <span className={styles.logoMark} aria-hidden="true">
        <img src="/logo.svg" alt={`${companyConfig.name} logo`} width="56" height="56" />
      </span>
      <span className={styles.logoText}>
        {companyConfig.name}
      </span>
    </div>
  );
}

function Cloud1() {
  return (
    <svg viewBox="0 0 400 250" className={styles.cloudSvg} role="img" aria-label="Cloud">
      <defs>
        <linearGradient id="cloudGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#e8f4ff" stopOpacity="0.85" />
        </linearGradient>
        <filter id="cloudShadow1" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="10" stdDeviation="15" floodColor="#0a1a42" floodOpacity="0.15" />
        </filter>
      </defs>
      <g filter="url(#cloudShadow1)">
        <ellipse cx="120" cy="160" rx="90" ry="60" fill="url(#cloudGrad1)" />
        <ellipse cx="200" cy="140" rx="100" ry="70" fill="url(#cloudGrad1)" />
        <ellipse cx="300" cy="155" rx="85" ry="55" fill="url(#cloudGrad1)" />
        <ellipse cx="160" cy="130" rx="70" ry="50" fill="url(#cloudGrad1)" />
        <ellipse cx="250" cy="120" rx="80" ry="55" fill="url(#cloudGrad1)" />
      </g>
    </svg>
  );
}

function Cloud2() {
  return (
    <svg viewBox="0 0 350 200" className={styles.cloudSvg} role="img" aria-label="Cloud">
      <defs>
        <linearGradient id="cloudGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#dbeafe" stopOpacity="0.8" />
        </linearGradient>
        <filter id="cloudShadow2" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#0a1a42" floodOpacity="0.12" />
        </filter>
      </defs>
      <g filter="url(#cloudShadow2)">
        <ellipse cx="100" cy="130" rx="75" ry="50" fill="url(#cloudGrad2)" />
        <ellipse cx="175" cy="115" rx="85" ry="55" fill="url(#cloudGrad2)" />
        <ellipse cx="260" cy="125" rx="70" ry="45" fill="url(#cloudGrad2)" />
        <ellipse cx="140" cy="100" rx="60" ry="40" fill="url(#cloudGrad2)" />
        <ellipse cx="220" cy="95" rx="65" ry="45" fill="url(#cloudGrad2)" />
      </g>
    </svg>
  );
}

function Airplane() {
  return (
    <svg viewBox="0 0 600 400" className={styles.airplaneSvg} role="img" aria-label="Airplane">
      <defs>
        <linearGradient id="bodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f8fbff" />
          <stop offset="50%" stopColor="#e8f0fa" />
          <stop offset="100%" stopColor="#d0dff0" />
        </linearGradient>
        <linearGradient id="wingGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f0f5fc" />
          <stop offset="100%" stopColor="#c8d8ec" />
        </linearGradient>
        <linearGradient id="engineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#4a5568" />
          <stop offset="50%" stopColor="#718096" />
          <stop offset="100%" stopColor="#4a5568" />
        </linearGradient>
        <filter id="planeShadow" x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="30" stdDeviation="25" floodColor="#0a1a42" floodOpacity="0.35" />
        </filter>
      </defs>
      <g filter="url(#planeShadow)" transform="rotate(-15, 300, 200)">
        {/* Fuselage */}
        <ellipse cx="300" cy="200" rx="220" ry="35" fill="url(#bodyGrad)" />
        <ellipse cx="300" cy="195" rx="210" ry="28" fill="#f5f9ff" opacity="0.6" />

        {/* Nose */}
        <path d="M80 200 Q60 200 70 195 L120 185 Q100 200 120 215 L70 205 Q60 200 80 200Z" fill="url(#bodyGrad)" />

        {/* Tail */}
        <path d="M500 200 L520 200 L540 150 L530 150 L515 185 L500 185Z" fill="url(#bodyGrad)" />
        <path d="M520 200 L540 200 L560 170 L545 170Z" fill="url(#wingGrad)" />

        {/* Vertical Stabilizer */}
        <path d="M510 200 L530 200 L545 130 L525 130 L515 180Z" fill="url(#bodyGrad)" />
        <path d="M515 195 L525 195 L535 145 L520 145Z" fill="#e8f0fa" opacity="0.7" />

        {/* Main Wings */}
        <path d="M250 210 L350 210 L420 320 L380 330 L320 250 L280 250 L220 330 L180 320Z" fill="url(#wingGrad)" />
        <path d="M260 215 L340 215 L390 290 L370 295 L320 240 L280 240 L230 295 L210 290Z" fill="#f0f5fc" opacity="0.5" />

        {/* Left Engine */}
        <ellipse cx="230" cy="255" rx="25" ry="15" fill="url(#engineGrad)" />
        <ellipse cx="225" cy="255" rx="12" ry="10" fill="#2d3748" />
        <ellipse cx="225" cy="255" rx="6" ry="5" fill="#1a202c" />

        {/* Right Engine */}
        <ellipse cx="370" cy="255" rx="25" ry="15" fill="url(#engineGrad)" />
        <ellipse cx="365" cy="255" rx="12" ry="10" fill="#2d3748" />
        <ellipse cx="365" cy="255" rx="6" ry="5" fill="#1a202c" />

        {/* Windows */}
        <g fill="#a0c4e8" opacity="0.8">
          <ellipse cx="150" cy="195" rx="4" ry="3" />
          <ellipse cx="170" cy="195" rx="4" ry="3" />
          <ellipse cx="190" cy="195" rx="4" ry="3" />
          <ellipse cx="210" cy="195" rx="4" ry="3" />
          <ellipse cx="230" cy="195" rx="4" ry="3" />
          <ellipse cx="250" cy="195" rx="4" ry="3" />
          <ellipse cx="270" cy="195" rx="4" ry="3" />
          <ellipse cx="290" cy="195" rx="4" ry="3" />
          <ellipse cx="310" cy="195" rx="4" ry="3" />
          <ellipse cx="330" cy="195" rx="4" ry="3" />
          <ellipse cx="350" cy="195" rx="4" ry="3" />
          <ellipse cx="370" cy="195" rx="4" ry="3" />
          <ellipse cx="390" cy="195" rx="4" ry="3" />
          <ellipse cx="410" cy="195" rx="4" ry="3" />
          <ellipse cx="430" cy="195" rx="4" ry="3" />
        </g>

        {/* Cockpit Windows */}
        <path d="M95 192 L115 188 L115 195 L100 198Z" fill="#6b9ac4" opacity="0.9" />
        <path d="M95 208 L115 212 L115 205 L100 202Z" fill="#6b9ac4" opacity="0.9" />
      </g>
    </svg>
  );
}

function Barcode() {
  return (
    <svg viewBox="0 0 60 220" className={styles.barcodeSvg} role="img" aria-label="Barcode">
      <defs>
        <linearGradient id="barcodeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0a1a42" />
          <stop offset="100%" stopColor="#1a2d5a" />
        </linearGradient>
      </defs>
      {/* Barcode lines with varying widths */}
      <rect x="5" y="5" width="4" height="210" fill="url(#barcodeGrad)" />
      <rect x="11" y="5" width="2" height="210" fill="url(#barcodeGrad)" />
      <rect x="15" y="5" width="5" height="210" fill="url(#barcodeGrad)" />
      <rect x="22" y="5" width="2" height="210" fill="url(#barcodeGrad)" />
      <rect x="26" y="5" width="3" height="210" fill="url(#barcodeGrad)" />
      <rect x="31" y="5" width="4" height="210" fill="url(#barcodeGrad)" />
      <rect x="37" y="5" width="2" height="210" fill="url(#barcodeGrad)" />
      <rect x="41" y="5" width="5" height="210" fill="url(#barcodeGrad)" />
      <rect x="48" y="5" width="2" height="210" fill="url(#barcodeGrad)" />
      <rect x="52" y="5" width="3" height="210" fill="url(#barcodeGrad)" />
    </svg>
  );
}
